import { AnimatePresence, motion } from 'framer-motion';
import React, { FC, forwardRef, HTMLAttributes, PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Listing } from '../../../api/listings-client';
import { DescriptionInput, TitleInput } from '../CreateListing/DescriptionStep';
import { ImageData, ImageItem, UploadBox, useImageArranger } from '../CreateListing/ImagesArranger';
import DetailsEditContent from './DetailsEditContent';
import { EditListingChanges } from './EditTypes';

interface DescriptionModalContentProps {
  editedDescription: string;
  setEditedDescription: (value: React.SetStateAction<string>) => void;
}

const DescriptionModalContent: React.FC<DescriptionModalContentProps> = ({ editedDescription, setEditedDescription }) => {
  return (
    <div>
      <DescriptionInput
        value={editedDescription}
        onChange={setEditedDescription}
        isDisabled={false}
        minLength={30}
        maxLength={5000}
        placeholder="Describe the property..."
      />
    </div>
  );
}

export enum ModalPrompt {
  Description,
  Images,
  Details
}

interface ScrollableContainerProps extends HTMLAttributes<HTMLDivElement> {
  gradientColor?: string;
  gradientHeight?: number;
}

const ScrollableContainer = forwardRef<HTMLDivElement, PropsWithChildren<ScrollableContainerProps>>(
  ({ children, className, gradientColor = 'white', gradientHeight = 32, style, ...rest }, forwardedRef) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [showGradient, setShowGradient] = useState(false);

    const checkScroll = () => {
      if (containerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
        const hasScroll = scrollHeight > clientHeight;
        const atBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight - 1; // Allow 1px tolerance
        console.log("Scroll info:", { scrollTop, clientHeight, scrollHeight, hasScroll, atBottom });
        setShowGradient(hasScroll && !atBottom);
      }
    };

    useEffect(() => {
      const container = containerRef.current;
      if (container) {
        container.addEventListener('scroll', checkScroll);

        // Use ResizeObserver to detect content changes
        const resizeObserver = new ResizeObserver(() => {
          console.log("Content resized");
          checkScroll();
        });
        resizeObserver.observe(container);

        // Initial check after a short delay to ensure content is rendered
        setTimeout(checkScroll, 0);

        return () => {
          container.removeEventListener('scroll', checkScroll);
          resizeObserver.disconnect();
        };
      }
    }, []);

    // Combine refs to provide access to the DOM element
    React.useImperativeHandle(forwardedRef, () => containerRef.current as HTMLDivElement);

    return (
      <div
        ref={containerRef}
        className={`relative scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100 ${className || ''}`}
        style={{
          ...style,
        }}
        {...rest}
      >
        {children}
        {showGradient && (
          <div
            className="sticky bottom-[-1px] left-0 right-0 z-50 pointer-events-none"
            style={{
              height: `${gradientHeight}px`,
              background: `linear-gradient(to top, ${gradientColor}, transparent)`,
              marginTop: `-${gradientHeight}px`
            }}
          />
        )}
      </div>
    );
  }
);
interface EditModalProps {
  open: boolean;
  onClose: () => void;
  currentPrompt: ModalPrompt;
  localListing: Listing | null;
  onSubmit: (changes: EditListingChanges) => Promise<void>;
  initialDetailsTab?: 'Facilities' | 'Pricing' | 'Time';
  initialImages: ImageData[];
}

const modalTitle = (prompt: ModalPrompt): string => {
  switch (prompt) {
    case ModalPrompt.Description:
      return "Description";
    case ModalPrompt.Images:
      return "Images";
    case ModalPrompt.Details:
      return "Edit Details";
    default:
      return "Edit";
  }
};

const MemoizedImageItem = React.memo(ImageItem);
const MemoizedUploadBox = React.memo(UploadBox);

interface UseImagesModalContentProps {
  boxStates: {
    imageBoxStates: any[];
    uploadBoxState: { x: number; y: number };
  };
  draggingId: string | null;
  enableTransitions: boolean;
  handleDragStart: (boxId: string, clientX: number, clientY: number) => void;
  handleDelete: (boxId: string) => void;
  handleImageUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  containerStyle: () => React.CSSProperties;
  containerRef: React.RefObject<HTMLDivElement>;
}

export const useImagesModalContent = ({
  boxStates,
  draggingId,
  enableTransitions,
  handleDragStart,
  handleDelete,
  handleImageUpload,
  containerStyle,
  containerRef,
}: UseImagesModalContentProps) => {
  return useMemo(() => {
    const containerWidth = containerRef.current?.offsetWidth || 0;
    const getColumnCount = (containerWidth: number): number => {
      if (containerWidth < 500) return 2;
      if (containerWidth < 700) return 3;
      return 4;
    };
    const columns = getColumnCount(containerWidth);
    const padding = 20;
    const boxWidth = (containerWidth - (columns + 1) * padding) / columns;

    return (
      <div
        style={containerStyle()}
        className="relative"
      >
        {boxStates.imageBoxStates.slice().sort((a, b) => a.renderId - b.renderId).map((state) => (
          <MemoizedImageItem
            key={state.imageData.id}
            width={boxWidth}
            height={boxWidth}
            boxId={state.imageData.id}
            imageBoxState={state}
            imageData={state.imageData}
            onDragStart={handleDragStart}
            onDelete={handleDelete}
            isDragging={state.imageData.id === draggingId}
            enableTransitions={enableTransitions}
          />
        ))}
        {containerRef.current && (
          <MemoizedUploadBox
            width={boxWidth}
            height={boxWidth}
            onUpload={handleImageUpload}
            enableTransitions={enableTransitions}
            state={boxStates.uploadBoxState}
          />
        )}
      </div>
    );
  }, [boxStates, draggingId, enableTransitions, handleDelete, handleDragStart, handleImageUpload, containerStyle, containerRef]);
};

const EditModal: FC<EditModalProps> = ({
  open,
  onClose,
  currentPrompt,
  localListing,
  onSubmit,
  initialDetailsTab = 'Facilities',
  initialImages
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [editedListingChanges, setEditedListingChanges] = useState<EditListingChanges>({});
  const [submitState, setSubmitState] = useState<'idle' | 'submitting' | 'disabled'>('idle');

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 200);
  };

  const updateEditedListing = (field: keyof EditListingChanges, value: any) => {
    setEditedListingChanges(prev => {
      console.log("prev edit listing ", prev);
      console.log("new edit listing ", { ...prev, [field]: value });
      return ({ ...prev, [field]: value });
    });
  };

  const handleSubmit = async () => {
    setSubmitState('submitting');
    try {
      await onSubmit(editedListingChanges);
      handleClose();
    } catch (err) {
      console.error("Error submitting changes:", err);
      // Handle error (e.g., show error message)
    } finally {
      setSubmitState('idle');
    }
  };

  const onImagesUpdate = useCallback((images: ImageData[]) => {
    console.log("on images update ", images);
    const convertedImages = images.map((image) => ({
      mediaId: image.mediaId,
      image: image.image,
    }));
    console.log("on images update ", convertedImages);
    updateEditedListing('images', convertedImages);
  }, []);

  const {
    draggingId,
    boxStates,
    handleImageUpload,
    handleDragStart,
    handleDelete,
    containerStyle,
    setIsMounted,
    containerRef,
    enableTransitions,
    resetArrangement,
  } = useImageArranger({ initialImages: initialImages, onImagesUpdate });

  const imagesContent = useImagesModalContent({
    boxStates,
    draggingId,
    enableTransitions,
    handleDragStart,
    handleDelete,
    handleImageUpload,
    containerStyle,
    containerRef,
  });

  // New function to check if there are any uploading or error images
  const hasUploadingOrErrorImages = useMemo(() => {
    return boxStates.imageBoxStates.some(state => state.isUploading || state.isError);
  }, [boxStates.imageBoxStates]);

  // Update submit button state based on image upload status
  useEffect(() => {
    if (hasUploadingOrErrorImages) {
      setSubmitState('disabled');
    } else {
      setSubmitState('idle');
    }
  }, [hasUploadingOrErrorImages]);

  useEffect(() => {
    if (open) {
      setEditedListingChanges({});
      setSubmitState('idle');

      resetArrangement();
    }
    setIsMounted(open);
  }, [open]);


  const renderModalContent = () => {
    if (!localListing) return null;

    switch (currentPrompt) {
      case ModalPrompt.Description:
        return (
          <DescriptionModalContent
            editedDescription={localListing.description}
            setEditedDescription={(value) => updateEditedListing('description', value)}
          />
        );
      case ModalPrompt.Images:
        return imagesContent;
      case ModalPrompt.Details:
        return (
          <DetailsEditContent
            initialTab={initialDetailsTab}
            listing={localListing}
            updateEditListing={updateEditedListing}
          />
        );
      default:
        return null;
    }
  };

  return (
    <AnimatePresence>
      {(open && !isClosing) && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            aria-hidden="true"
            onClick={handleClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />

          <motion.div
            className="bg-white rounded-xl text-left overflow-hidden shadow-xl w-full sm:max-w-4xl relative z-[90] h-full sm:h-auto sm:max-h-[90vh] flex flex-col"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{
              type: "tween",
              duration: 0.3,
              ease: "easeInOut"
            }}
          >
            <div className="relative border-b border-gray-200">
              <div className="text-center py-5">
                <h2 className="text-xl font-medium text-gray-900">{modalTitle(currentPrompt)}</h2>
              </div>
              <button
                type="button"
                className="absolute top-4 right-4 flex justify-center items-center size-8 text-sm font-semibold rounded-full border border-transparent text-gray-500 hover:bg-gray-100 transition-colors duration-200 ease-in-out"
                onClick={handleClose}
              >
                <span className="sr-only">Close</span>
                <svg className="flex-shrink-0 size-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
              </button>
            </div>

            <ScrollableContainer className="sm:m-6 overflow-y-auto flex-grow isolate" ref={containerRef}>
              {renderModalContent()}
            </ScrollableContainer>

            <div className="flex justify-end p-4 border-t border-gray-200">
              <button
                type="button"
                className="py-3 px-7 inline-flex items-center gap-x-2 text-sm font-semibold rounded-lg border border-transparent bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:pointer-events-none"
                onClick={handleSubmit}
                disabled={submitState === 'submitting' || submitState === 'disabled'}
              >
                {submitState === 'submitting' ? (
                  <>
                    <span className="animate-spin inline-block size-4 border-[2px] border-current border-t-transparent text-white rounded-full" role="status" aria-label="loading"></span>
                    Submitting...
                  </>
                ) : (
                  'Submit'
                )}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EditModal;
